import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import * as authApi from '@/api/auth';
import type { CurrentUser } from '@/api/auth';
import { setSessionLostHandler } from '@/api/client';
import { AuthContext, type AuthStatus } from './AuthContext';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<CurrentUser | null>(null);

  useEffect(() => {
    let active = true;

    authApi.restoreSession().then((restored) => {
      if (!active) return;
      setUser(restored);
      setStatus(restored ? 'authenticated' : 'anonymous');
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setSessionLostHandler(() => {
      setUser(null);
      setStatus('anonymous');
    });

    return () => setSessionLostHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const loggedIn = await authApi.login(email, password);
    setUser(loggedIn);
    setStatus('authenticated');
    return loggedIn;
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo(() => ({ status, user, login, logout }), [status, user, login, logout]);

  return <AuthContext value={value}>{children}</AuthContext>;
};
